import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Castle, Image as ImageIcon, Layers, MapPinned, Pipette, RefreshCw, Save, Trash2 } from 'lucide-react';
import adminService from '../../services/adminService';
import { normalizeMiniGameNaveAssets, parseMiniGameAssetList } from '../rpg/miniGameNaveUtils';

const CELL_SIZE = 32;
const ROLE_FALLBACK_COLORS = {
  terrain: ['#31452a', '#4a6643', '#59724f', '#6c7f5b'],
  props: ['#5b7b62', '#6f9278', '#86a18d', '#a0b6a3'],
  buildings: ['#6d5b4f', '#836956', '#977864', '#ab8a73'],
  objects: ['#6a7060', '#7d856f', '#90977c', '#a0a987'],
  resources: ['#64794d', '#7a8c60', '#90a074', '#a8b68a'],
  characters: ['#4a6d86', '#5d7f97', '#728faa', '#8ca8ba'],
  npc: ['#5f6486', '#7378a0', '#878bc0', '#a1a6db']
};

const INTERACTION_MARKER_FILL = '#ffffff';
const INTERACTION_MARKER_STROKE = '#facc15';

function drawInteractionTriangle(ctx, x, y, size) {
  const triangleWidth = Math.max(12, Math.round(size * 0.42));
  const triangleHeight = Math.max(10, Math.round(size * 0.28));
  const centerX = x + size / 2;
  const centerY = y + size / 2;
  const topY = centerY - triangleHeight / 2;
  const bottomY = centerY + triangleHeight / 2;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(centerX, topY);
  ctx.lineTo(centerX - triangleWidth / 2, bottomY);
  ctx.lineTo(centerX + triangleWidth / 2, bottomY);
  ctx.closePath();
  ctx.fillStyle = INTERACTION_MARKER_FILL;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = INTERACTION_MARKER_STROKE;
  ctx.stroke();
  ctx.restore();
}

const DEFAULT_ROLES = ['terrain', 'buildings', 'props', 'objects', 'resources', 'characters', 'npc'];
const INTERACTION_TYPES = [
  { value: 'city_shop', label: 'Tienda', target: '/city/shop' },
  { value: 'city_workshop', label: 'Taller', target: '/city/workshop' },
  { value: 'city_warehouse', label: 'Almacen', target: '/city/warehouse' },
  { value: 'city_hangar', label: 'Hangar', target: '/city/hangar' },
  { value: 'city_transport', label: 'Transporte', target: '/city/transport' },
  { value: 'mini-juego-nave', label: 'Mini-juego nave', target: '/rpg/interior/mini-juego-nave' },
  { value: 'mini-juego-tragaperra', label: 'Mini-juego tragaperra', target: '/rpg/interior/mini-juego-tragaperra' },
  { value: 'door', label: 'Puerta', target: '' },
  { value: 'entry', label: 'Entrada', target: '' },
  { value: 'portal', label: 'Portal', target: '' },
  { value: 'plot', label: 'Parcela', target: '' },
  { value: 'defense', label: 'Defensa', target: '' },
  { value: 'note', label: 'Mensaje', target: '' }
];

const ACCESS_DIRECTIONS = [
  { value: 'any', label: 'Cualquier lado' },
  { value: 'north', label: 'Norte' },
  { value: 'south', label: 'Sur' },
  { value: 'east', label: 'Este' },
  { value: 'west', label: 'Oeste' }
];

function emptyInteractionState() {
  return {
    enabled: false,
    type: 'city_shop',
    label: '',
    description: '',
    target: '/city/shop',
    access_from: 'any',
    game_assets: normalizeMiniGameNaveAssets()
  };
}

function normalizeInteractionMetadata(interaction) {
  if (!interaction || typeof interaction !== 'object') return null;
  if (interaction.enabled === false) return null;
  const type = String(interaction.type || 'note').trim() || 'note';
  const label = String(interaction.label || '').trim();
  const description = String(interaction.description || '').trim();
  const target = String(interaction.target || '').trim();
  const accessFrom = String(interaction.access_from || 'any').trim() || 'any';
  const gameAssets = normalizeMiniGameNaveAssets(interaction.game_assets);
  if (!label && !description && !target && !type) return null;
  return {
    enabled: true,
    type,
    label,
    description,
    target,
    access_from: accessFrom,
    game_assets: gameAssets
  };
}

function normalizeTile(tile) {
  return {
    coordinate: Number(tile.coordinate) || 0,
    layer: Number(tile.layer) || 0,
    tileset_role: String(tile.tileset_role || tile.tilesetKey || 'terrain').trim() || 'terrain',
    tile_index: Number(tile.tile_index) || 0,
    blocked: Boolean(tile.blocked),
    metadata: tile.metadata && typeof tile.metadata === 'object' ? tile.metadata : {}
  };
}

function makeTileKey(coordinate, layer) {
  return `${Number(layer) || 0}:${Number(coordinate) || 0}`;
}

function normalizeRectSelection(start, end, width, height) {
  if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
  const startX = Math.max(0, Math.min(width - 1, start % width));
  const startY = Math.max(0, Math.min(height - 1, Math.floor(start / width)));
  const endX = Math.max(0, Math.min(width - 1, end % width));
  const endY = Math.max(0, Math.min(height - 1, Math.floor(end / width)));
  return {
    x1: Math.min(startX, endX),
    y1: Math.min(startY, endY),
    x2: Math.max(startX, endX),
    y2: Math.max(startY, endY)
  };
}

function getTileSourceRect(tileIndex, columnsCount, tileSize) {
  const cols = Math.max(1, Number(columnsCount) || 1);
  const index = Math.max(0, Number(tileIndex) || 0);
  return {
    sx: (index % cols) * tileSize,
    sy: Math.floor(index / cols) * tileSize
  };
}

function getRoleColor(role, tileIndex) {
  const palette = ROLE_FALLBACK_COLORS[role] || ROLE_FALLBACK_COLORS.terrain;
  return palette[Math.abs(Number(tileIndex) || 0) % palette.length];
}

function resolveRpgTilesetImageSrc(image) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('blob:') || image.startsWith('/')) return image;
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';
  return `${apiBaseUrl}/uploads/gallery/${image}`;
}

function normalizeCatalogEntry(entry) {
  return {
    name: String(entry?.name || '').trim(),
    category: String(entry?.category || 'general').trim() || 'general',
    tile_index: Number(entry?.tile_index) || 0,
    blocked: entry?.blocked === false || Number(entry?.blocked) === 0 ? 0 : 1,
    description: String(entry?.description || '').trim()
  };
}

function getTilesetCatalog(tileset) {
  if (!tileset) return [];
  if (Array.isArray(tileset.catalog) && tileset.catalog.length) {
    return tileset.catalog.map(normalizeCatalogEntry);
  }
  const metadata = tileset.metadata && typeof tileset.metadata === 'object' ? tileset.metadata : {};
  const source = Array.isArray(metadata.catalog)
    ? metadata.catalog
    : Array.isArray(metadata.tiles)
      ? metadata.tiles
      : [];
  return source.map(normalizeCatalogEntry);
}

function TilePalette({ tileset, selectedTileIndex, onSelectTile }) {
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [paletteZoom, setPaletteZoom] = useState(2.2);
  const [paletteMode, setPaletteMode] = useState('catalog');
  const imageSrc = useMemo(() => resolveRpgTilesetImageSrc(tileset?.image), [tileset?.image]);
  const tileSize = Math.max(1, Number(tileset?.tile_size) || 48);
  const columnsCount = Math.max(1, Number(tileset?.columns_count) || 0 || Math.floor((imageSize.width || tileSize) / tileSize) || 1);
  const rowsCount = Math.max(1, Math.floor((imageSize.height || tileSize) / tileSize) || 1);
  const totalTiles = Math.max(1, columnsCount * rowsCount);
  const previewSize = Math.max(56, Math.round(tileSize * paletteZoom));
  const catalog = useMemo(() => getTilesetCatalog(tileset), [tileset]);
  const selectedCatalogEntry = catalog.find((entry) => Number(entry.tile_index) === Number(selectedTileIndex)) || null;
  const hasCatalog = catalog.length > 0;

  const getPreviewStyle = (tileIndex, size) => {
    const rect = getTileSourceRect(tileIndex, columnsCount, tileSize);
    const zoom = size / tileSize;
    return {
      backgroundImage: `url(${imageSrc})`,
      backgroundPosition: `-${rect.sx * zoom}px -${rect.sy * zoom}px`,
      backgroundSize: `${columnsCount * size}px ${rowsCount * size}px`
    };
  };

  useEffect(() => {
    if (!tileset?.image) {
      setImageSize({ width: 0, height: 0 });
      return undefined;
    }

    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) {
        setImageSize({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
      }
    };
    img.onerror = () => {
      if (!cancelled) setImageSize({ width: 0, height: 0 });
    };
    img.src = imageSrc;

    return () => {
      cancelled = true;
    };
  }, [imageSrc, tileset?.image]);

  useEffect(() => {
    if (!hasCatalog) {
      setPaletteMode('sheet');
      return;
    }
    setPaletteMode((current) => (current === 'sheet' ? 'sheet' : 'catalog'));
  }, [hasCatalog, tileset?.id]);

  if (!tileset) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-4 text-sm text-slate-500">
        No hay tileset disponible para esta capa.
      </div>
    );
  }

  if (!tileset.image) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-4 text-sm text-slate-500">
        Este tileset no tiene imagen. Usa la galeria para asignar un tileset del mapa.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-white">{tileset.name}</div>
          <div className="text-[11px] text-slate-500">
            {columnsCount} columnas · {rowsCount} filas · {totalTiles} tiles
          </div>
        </div>
        <div className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-300">
          {tileset.role}
        </div>
      </div>

      <div className="grid grid-cols-[96px_1fr] gap-3 items-start">
        <div className="space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Tile elegido</div>
          <div className="rounded-2xl border border-game-primary/40 bg-slate-950/70 p-2">
            <div
              className="mx-auto rounded-xl border border-slate-700 bg-slate-900 bg-no-repeat"
              style={{
                width: '72px',
                height: '72px',
                ...getPreviewStyle(selectedTileIndex, 72)
              }}
            />
          </div>
          <div className="space-y-1 text-center">
            <div className="text-[11px] text-cyan-300 font-bold">
              {selectedCatalogEntry?.name || `Tile ${selectedTileIndex}`}
            </div>
            {selectedCatalogEntry?.category ? (
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {selectedCatalogEntry.category}
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Tamano de vista</div>
            <div className="text-[11px] text-slate-300">{Math.round(previewSize)} px</div>
          </div>
          <input
            type="range"
            min="1.2"
            max="3.6"
            step="0.2"
            value={paletteZoom}
            onChange={(e) => setPaletteZoom(Number(e.target.value) || 2.2)}
            className="w-full accent-cyan-400"
          />
          {hasCatalog && (
            <div className="flex gap-2 rounded-2xl border border-slate-800 bg-slate-950/60 p-1">
              <button
                type="button"
                onClick={() => setPaletteMode('catalog')}
                className={`flex-1 rounded-xl px-3 py-2 text-xs font-bold transition-colors ${
                  paletteMode === 'catalog' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Catalogo
              </button>
              <button
                type="button"
                onClick={() => setPaletteMode('sheet')}
                className={`flex-1 rounded-xl px-3 py-2 text-xs font-bold transition-colors ${
                  paletteMode === 'sheet' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Tileset completo
              </button>
            </div>
          )}
        </div>
      </div>

      {hasCatalog && paletteMode === 'catalog' ? (
        <div className="max-h-[460px] overflow-auto rounded-2xl border border-slate-800 bg-slate-950/60 p-2">
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))' }}>
            {catalog.map((entry) => {
              const isSelected = Number(selectedTileIndex) === Number(entry.tile_index);
              const cardSize = 88;
              return (
                <button
                  key={`${tileset.id}-${entry.tile_index}-${entry.name || 'tile'}`}
                  type="button"
                  onClick={() => onSelectTile(Number(entry.tile_index) || 0, {
                    ...entry,
                    tileset_id: tileset.tileset_id || tileset.id || 0,
                    tileset_role: tileset.role,
                    tileset_assignment_key: tileset.assignment_key || ''
                  })}
                  className={`overflow-hidden rounded-xl border p-2 text-left transition-all ${
                    isSelected
                      ? 'border-game-primary ring-1 ring-game-primary/40 bg-game-primary/10'
                      : 'border-slate-800 bg-slate-900/60 hover:border-slate-600'
                  }`}
                  title={entry.name || `Tile ${entry.tile_index}`}
                >
                  <div
                    className="mx-auto rounded-lg border border-slate-700 bg-slate-900 bg-no-repeat"
                    style={{
                      width: `${cardSize}px`,
                      height: `${cardSize}px`,
                      ...getPreviewStyle(entry.tile_index, cardSize)
                    }}
                  />
                  <div className="mt-2 space-y-1">
                    <div className="truncate text-sm font-bold text-white">
                      {entry.name || `Tile ${entry.tile_index}`}
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-slate-500">
                      <span>{entry.category}</span>
                      <span className={entry.blocked ? 'text-amber-300' : 'text-emerald-300'}>
                        {entry.blocked ? 'Bloquea' : 'Libre'}
                      </span>
                    </div>
                    {entry.description ? (
                      <div className="max-h-10 overflow-hidden text-[11px] leading-snug text-slate-400">
                        {entry.description}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="max-h-[420px] overflow-auto rounded-2xl border border-slate-800 bg-slate-950/60 p-2">
          <div
            className="grid gap-1"
            style={{
              gridTemplateColumns: `repeat(${columnsCount}, ${previewSize}px)`
            }}
          >
            {Array.from({ length: totalTiles }).map((_, index) => {
              const isSelected = Number(selectedTileIndex) === index;
              return (
                <button
                  key={`${tileset.id}-${index}`}
                  type="button"
                  onClick={() => onSelectTile(index, {
                    tileset_id: tileset.tileset_id || tileset.id || 0,
                    tileset_role: tileset.role,
                    tileset_assignment_key: tileset.assignment_key || ''
                  })}
                  className={`overflow-hidden rounded-lg border transition-all shrink-0 ${
                    isSelected
                      ? 'border-game-primary ring-1 ring-game-primary/40 bg-game-primary/10'
                      : 'border-slate-800 bg-slate-900/60 hover:border-slate-600'
                  }`}
                  title={`Tile ${index}`}
                  style={{
                    width: `${previewSize}px`,
                    height: `${previewSize}px`
                  }}
                >
                  <div
                    className="h-full w-full bg-cover bg-no-repeat"
                    style={{
                      ...getPreviewStyle(index, previewSize)
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RpgCityMapEditor({
  city,
  config,
  onSaveMap,
  isSaving = false
}) {
  const canvasRef = useRef(null);
  const [loadedCityId, setLoadedCityId] = useState(null);
  const [mapWidth, setMapWidth] = useState(32);
  const [mapHeight, setMapHeight] = useState(20);
  const [tiles, setTiles] = useState([]);
  const [activeTilesetKey, setActiveTilesetKey] = useState('');
  const [activeLayer, setActiveLayer] = useState(0);
  const [selectedTileIndex, setSelectedTileIndex] = useState(0);
  const [paintBlocked, setPaintBlocked] = useState(false);
  const [activeTool, setActiveTool] = useState('paint');
  const [sidebarTab, setSidebarTab] = useState('tileset');
  const [hoverCoordinate, setHoverCoordinate] = useState(null);
  const [dragPainting, setDragPainting] = useState(false);
  const [dragStartCoordinate, setDragStartCoordinate] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null);
  const [interactionState, setInteractionState] = useState(() => emptyInteractionState());
  const [interactionTarget, setInteractionTarget] = useState(null);
  const [undoDepth, setUndoDepth] = useState(0);
  const undoStackRef = useRef([]);
  const strokeUndoCapturedRef = useRef(false);
  const imagesRef = useRef({});
  const imageInfoRef = useRef({});

  const { data: mapData, isLoading, refetch } = useQuery(
    ['adminRpgCityMap', city?.id],
    () => adminService.getRpgCityMap(city.id),
    {
      enabled: !!city?.id,
      refetchOnWindowFocus: false
    }
  );

  const assignedTilesets = useMemo(() => {
    const links = Array.isArray(config?.tilesets) ? config.tilesets : [];
    return links
      .filter((link) => link?.is_enabled !== false && link?.tileset)
      .map((link) => ({
        tileset_id: Number(link.tileset?.id) || Number(link.tileset_id) || 0,
        role: String(link.role || 'terrain'),
        sort_order: Number(link.sort_order) || 0,
        assignment_key: `${Number(link.tileset?.id) || Number(link.tileset_id) || 0}:${String(link.role || 'terrain')}:${Number(link.sort_order) || 0}`,
        ...link.tileset
      }));
  }, [config?.tilesets]);

  const tilesetByKey = useMemo(() => {
    const map = new Map();
    assignedTilesets.forEach((tileset) => {
      map.set(tileset.assignment_key, tileset);
    });
    return map;
  }, [assignedTilesets]);

  const tilesetById = useMemo(() => {
    const map = new Map();
    assignedTilesets.forEach((tileset) => {
      if (tileset.tileset_id) {
        map.set(Number(tileset.tileset_id), tileset);
      }
    });
    return map;
  }, [assignedTilesets]);

  const activeTileset = tilesetByKey.get(activeTilesetKey) || assignedTilesets[0] || null;
  const activeRoleValue = activeTileset?.role || 'terrain';

  const captureUndoSnapshot = () => {
    undoStackRef.current.push({
      tiles: tiles.map(normalizeTile),
      width: Number(mapWidth) || 32,
      height: Number(mapHeight) || 20
    });
    setUndoDepth(undoStackRef.current.length);
  };

  const undoLastChange = () => {
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    setTiles(previous.tiles.map(normalizeTile));
    setMapWidth(Number(previous.width) || 32);
    setMapHeight(Number(previous.height) || 20);
    setUndoDepth(undoStackRef.current.length);
  };

  const getTileAtCoordinate = (coordinate) => {
    const matches = tiles
      .filter((tile) => Number(tile.coordinate) === Number(coordinate))
      .sort((a, b) => Number(b.layer) - Number(a.layer));
    return matches[0] || null;
  };

  useEffect(() => {
    if (!city?.id || !mapData?.map) return;
    if (loadedCityId === Number(city.id)) return;

    setLoadedCityId(Number(city.id));
    setMapWidth(Number(mapData.map.width) || 32);
    setMapHeight(Number(mapData.map.height) || 20);
    setTiles(Array.isArray(mapData.map.tiles) ? mapData.map.tiles.map(normalizeTile) : []);
    undoStackRef.current = [];
    setUndoDepth(0);

    const firstTileset = assignedTilesets.find((tileset) => tileset.assignment_key)
      || assignedTilesets[0]
      || null;
    setActiveTilesetKey(firstTileset?.assignment_key || '');
    const firstCatalogIndex = getTilesetCatalog(firstTileset)[0]?.tile_index;
    setSelectedTileIndex(Number.isInteger(Number(firstCatalogIndex)) ? Number(firstCatalogIndex) : 0);
    setActiveLayer(0);
  }, [assignedTilesets, city?.id, loadedCityId, mapData]);

  useEffect(() => {
    if (!activeTileset) return;
    if (!tilesetByKey.has(activeTileset.assignment_key)) {
      setActiveTilesetKey(assignedTilesets[0]?.assignment_key || '');
    }
  }, [activeTileset, activeTilesetKey, assignedTilesets, tilesetByKey]);

  useEffect(() => {
    if (!assignedTilesets.length) return;
    if (activeTileset) return;
    setActiveTilesetKey(assignedTilesets[0].assignment_key || '');
  }, [activeTileset, assignedTilesets]);

  useEffect(() => {
    if (activeTool !== 'paint' && dragPainting) {
      setDragPainting(false);
    }
  }, [activeTool, dragPainting]);

  useEffect(() => {
    if (activeTool !== 'select' && selectionRect) {
      setSelectionRect(null);
      setDragStartCoordinate(null);
    }
  }, [activeTool, selectionRect]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = Math.max(1, Number(mapWidth) || 32);
    const height = Math.max(1, Number(mapHeight) || 20);
    canvas.width = width * CELL_SIZE;
    canvas.height = height * CELL_SIZE;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#050714';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const tileByKey = new Map();
    tiles.forEach((tile) => {
      tileByKey.set(makeTileKey(tile.coordinate, tile.layer), tile);
    });

    for (let layer = 0; layer <= 2; layer += 1) {
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const coordinate = (y * width) + x;
          const tile = tileByKey.get(makeTileKey(coordinate, layer));
          if (!tile) continue;

          const metadataTilesetId = Number(tile.metadata?.tileset_id) || 0;
          const metadataAssignmentKey = String(tile.metadata?.tileset_assignment_key || '').trim();
          const tileset = (metadataTilesetId && tilesetById.get(metadataTilesetId))
            || (metadataAssignmentKey && tilesetByKey.get(metadataAssignmentKey))
            || assignedTilesets.find((entry) => entry.role === tile.tileset_role && entry.sort_order === Number(tile.metadata?.sort_order))
            || assignedTilesets.find((entry) => entry.role === tile.tileset_role)
            || activeTileset
            || assignedTilesets[0]
            || null;
          const tilesetRefKey = tileset ? String(tileset.tileset_id || tileset.id || '') : '';
          const image = tilesetRefKey ? imagesRef.current[tilesetRefKey] : null;
          const imageInfo = tilesetRefKey ? imageInfoRef.current[tilesetRefKey] : null;
          const tileSize = Math.max(1, Number(tileset?.tile_size) || 48);
          const columns = Math.max(1, Number(tileset?.columns_count) || imageInfo?.columns || 1);
          const { sx, sy } = getTileSourceRect(tile.tile_index, columns, tileSize);
          const dx = x * CELL_SIZE;
          const dy = y * CELL_SIZE;

          if (image && image.complete && image.naturalWidth > 0) {
            try {
              ctx.drawImage(image, sx, sy, tileSize, tileSize, dx, dy, CELL_SIZE, CELL_SIZE);
            } catch (_err) {
              ctx.fillStyle = getRoleColor(tile.tileset_role, tile.tile_index);
              ctx.fillRect(dx, dy, CELL_SIZE, CELL_SIZE);
            }
          } else {
            ctx.fillStyle = getRoleColor(tile.tileset_role, tile.tile_index);
            ctx.fillRect(dx, dy, CELL_SIZE, CELL_SIZE);
          }

          if (tile.blocked) {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
            ctx.fillRect(dx, dy, CELL_SIZE, CELL_SIZE);
          }
        }
      }
    }

    tiles.forEach((tile) => {
      const interaction = tile?.metadata?.interaction;
      if (!interaction || interaction.enabled === false) return;
      const coordinate = Number(tile.coordinate);
      if (!Number.isInteger(coordinate)) return;
      const x = coordinate % width;
      const y = Math.floor(coordinate / width);
      drawInteractionTriangle(ctx, x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE);
    });

    if (hoverCoordinate !== null) {
      const hx = hoverCoordinate % width;
      const hy = Math.floor(hoverCoordinate / width);
      ctx.strokeStyle = '#22D3EE';
      ctx.lineWidth = 2;
      ctx.strokeRect(hx * CELL_SIZE + 1, hy * CELL_SIZE + 1, CELL_SIZE - 2, CELL_SIZE - 2);
    }

    if (selectionRect) {
      ctx.save();
      ctx.strokeStyle = '#A78BFA';
      ctx.fillStyle = 'rgba(167, 139, 250, 0.14)';
      ctx.lineWidth = 2;
      const sx = selectionRect.x1 * CELL_SIZE;
      const sy = selectionRect.y1 * CELL_SIZE;
      const sw = (selectionRect.x2 - selectionRect.x1 + 1) * CELL_SIZE;
      const sh = (selectionRect.y2 - selectionRect.y1 + 1) * CELL_SIZE;
      ctx.fillRect(sx, sy, sw, sh);
      ctx.strokeRect(sx + 1, sy + 1, sw - 2, sh - 2);
      ctx.restore();
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= width; x += 1) {
      ctx.beginPath();
      ctx.moveTo(x * CELL_SIZE, 0);
      ctx.lineTo(x * CELL_SIZE, height * CELL_SIZE);
      ctx.stroke();
    }
    for (let y = 0; y <= height; y += 1) {
      ctx.beginPath();
      ctx.moveTo(0, y * CELL_SIZE);
      ctx.lineTo(width * CELL_SIZE, y * CELL_SIZE);
      ctx.stroke();
    }
  }, [activeTileset, activeRoleValue, hoverCoordinate, mapHeight, mapWidth, tiles, tilesetById, assignedTilesets]);

  useEffect(() => {
    const roleImageLoaders = assignedTilesets.map((tileset) => {
      const tilesetRefKey = String(tileset.tileset_id || tileset.id || '');
      if (!tileset?.image) {
        imagesRef.current[tilesetRefKey] = null;
        imageInfoRef.current[tilesetRefKey] = { columns: Math.max(1, Number(tileset.columns_count) || 1), rows: 1 };
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => {
          imagesRef.current[tilesetRefKey] = img;
          const tileSize = Math.max(1, Number(tileset.tile_size) || 48);
          imageInfoRef.current[tilesetRefKey] = {
            columns: Math.max(1, Number(tileset.columns_count) || Math.floor((img.naturalWidth || tileSize) / tileSize) || 1),
            rows: Math.max(1, Math.floor((img.naturalHeight || tileSize) / tileSize) || 1)
          };
          resolve();
        };
        img.onerror = () => {
          imagesRef.current[tilesetRefKey] = null;
          imageInfoRef.current[tilesetRefKey] = { columns: Math.max(1, Number(tileset.columns_count) || 1), rows: 1 };
          resolve();
        };
        img.src = resolveRpgTilesetImageSrc(tileset.image);
      });
    });

    Promise.all(roleImageLoaders).then(() => {
      setTiles((current) => [...current]);
    });
  }, [assignedTilesets]);

  useEffect(() => {
    if (!activeTileset) return;
    const currentCatalog = getTilesetCatalog(activeTileset);
    const paletteLength = getPaletteLength(activeTileset, imageInfoRef.current[String(activeTileset?.tileset_id || activeTileset?.id || '')]);
    if (Number(selectedTileIndex) >= paletteLength) {
      setSelectedTileIndex(currentCatalog[0]?.tile_index ?? 0);
      return;
    }
    if (currentCatalog.length && !currentCatalog.some((entry) => Number(entry.tile_index) === Number(selectedTileIndex))) {
      setSelectedTileIndex(currentCatalog[0]?.tile_index ?? 0);
    }
  }, [activeTileset, selectedTileIndex]);

  function getPaletteLength(tileset, imageInfo) {
    if (!tileset) return 0;
    const tileSize = Math.max(1, Number(tileset.tile_size) || 48);
    const columns = Math.max(1, Number(tileset.columns_count) || imageInfo?.columns || 1);
    const rows = Math.max(1, imageInfo?.rows || 1);
    return Math.max(1, columns * rows);
  }

  const isMiniGameNave = interactionState.type === 'mini-juego-nave';
  const miniGameAssets = normalizeMiniGameNaveAssets(interactionState.game_assets);

  const updateMiniGameAssets = (patch) => {
    setInteractionState((current) => ({
      ...current,
      game_assets: {
        ...normalizeMiniGameNaveAssets(current.game_assets),
        ...patch
      }
    }));
  };

  const buildPaintMetadata = () => {
    if (!activeTileset) return {};
    const nextMetadata = {
      tileset_id: Number(activeTileset.tileset_id || activeTileset.id || 0),
      tileset_role: activeRoleValue,
      tileset_assignment_key: activeTileset.assignment_key || '',
      sort_order: Number(activeTileset.sort_order) || 0
    };
    const interaction = sidebarTab === 'interaction'
      ? normalizeInteractionMetadata(interactionState)
      : null;
    if (interaction) {
      nextMetadata.interaction = interaction;
    }
    return nextMetadata;
  };

  const updateTileAt = (coordinate) => {
    const width = Math.max(1, Number(mapWidth) || 32);
    const height = Math.max(1, Number(mapHeight) || 20);
    if (!Number.isInteger(coordinate) || coordinate < 0 || coordinate >= width * height) return;
    if (!activeTileset) return;

    setTiles((current) => {
      const next = current.filter((tile) => !(Number(tile.coordinate) === coordinate && Number(tile.layer) === Number(activeLayer)));
      if (activeTool !== 'erase') {
        next.push({
          coordinate,
          layer: Number(activeLayer) || 0,
          tileset_role: activeRoleValue,
          tile_index: Number(selectedTileIndex) || 0,
          blocked: Boolean(paintBlocked),
          metadata: buildPaintMetadata()
        });
      }
      return next.sort((a, b) => (Number(a.layer) - Number(b.layer)) || (Number(a.coordinate) - Number(b.coordinate)));
    });
  };

  const applyTileSelection = (rect, mode = 'paint') => {
    if (!rect) return;
    const width = Math.max(1, Number(mapWidth) || 32);
    const height = Math.max(1, Number(mapHeight) || 20);
    captureUndoSnapshot();
    setTiles((current) => {
      let next = current;
      for (let y = rect.y1; y <= rect.y2; y += 1) {
        for (let x = rect.x1; x <= rect.x2; x += 1) {
          const coordinate = y * width + x;
          next = next.filter((tile) => !(Number(tile.coordinate) === coordinate && Number(tile.layer) === Number(activeLayer)));
          if (mode !== 'erase') {
            next = [
              ...next,
              {
                coordinate,
                layer: Number(activeLayer) || 0,
                tileset_role: activeRoleValue,
                tile_index: Number(selectedTileIndex) || 0,
                blocked: Boolean(paintBlocked),
                metadata: buildPaintMetadata()
              }
            ];
          }
        }
      }
      return next.sort((a, b) => (Number(a.layer) - Number(b.layer)) || (Number(a.coordinate) - Number(b.coordinate)));
    });
  };

  const selectTile = (tileIndex, tileInfo = null) => {
    setSelectedTileIndex(Number(tileIndex) || 0);
    setActiveTool('paint');
    setInteractionTarget(null);
    if (tileInfo && typeof tileInfo === 'object') {
      const nextAssignmentKey = String(tileInfo.tileset_assignment_key || '').trim();
      const nextTilesetId = Number(tileInfo.tileset_id) || 0;
      if (nextAssignmentKey && tilesetByKey.has(nextAssignmentKey)) {
        const nextTileset = tilesetByKey.get(nextAssignmentKey);
        setActiveTilesetKey(nextTileset.assignment_key || '');
      } else if (nextTilesetId && tilesetById.has(nextTilesetId)) {
        const nextTileset = tilesetById.get(nextTilesetId);
        setActiveTilesetKey(nextTileset.assignment_key || '');
      } else {
        const nextRole = String(tileInfo.tileset_role || tileInfo.role || '').trim();
        if (nextRole) {
          const fallbackTileset = assignedTilesets.find((tileset) => tileset.role === nextRole) || null;
          if (fallbackTileset) {
            setActiveTilesetKey(fallbackTileset.assignment_key || '');
          }
        }
      }
      if (Object.prototype.hasOwnProperty.call(tileInfo, 'blocked')) {
        setPaintBlocked(Boolean(Number(tileInfo.blocked)));
      }
      const nextInteraction = normalizeInteractionMetadata(tileInfo.interaction || tileInfo.metadata?.interaction);
      if (nextInteraction) {
        setInteractionState({
          enabled: true,
          type: nextInteraction.type || 'note',
          label: nextInteraction.label || '',
          description: nextInteraction.description || '',
          target: nextInteraction.target || '',
          access_from: nextInteraction.access_from || 'any',
          game_assets: normalizeMiniGameNaveAssets(nextInteraction.game_assets)
        });
      } else {
        setInteractionState(emptyInteractionState());
      }
    }
  };

  const hydrateEditingStateFromTile = (tile) => {
    if (!tile) return;
    const tileTilesetId = Number(tile.metadata?.tileset_id) || 0;
    const tileAssignmentKey = String(tile.metadata?.tileset_assignment_key || '').trim();
    const tileRole = String(tile.metadata?.tileset_role || tile.tileset_role || 'terrain').trim() || 'terrain';
    const targetCoordinate = Number(tile.coordinate) || 0;
    const targetLayer = Number(tile.layer) || 0;
    const nextTileset = (tileAssignmentKey && tilesetByKey.get(tileAssignmentKey))
      || (tileTilesetId && tilesetById.get(tileTilesetId))
      || assignedTilesets.find((entry) => entry.role === tileRole)
      || null;
    if (nextTileset) {
      setActiveTilesetKey(nextTileset.assignment_key || '');
    }
    setInteractionTarget({ coordinate: targetCoordinate, layer: targetLayer });
    setActiveLayer(Number(tile.layer) || 0);
    setSelectedTileIndex(Number(tile.tile_index) || 0);
    setPaintBlocked(Boolean(tile.blocked));
    const nextInteraction = normalizeInteractionMetadata(tile.metadata?.interaction);
    setInteractionState(nextInteraction
      ? {
          enabled: true,
          type: nextInteraction.type || 'note',
          label: nextInteraction.label || '',
          description: nextInteraction.description || '',
          target: nextInteraction.target || '',
          access_from: nextInteraction.access_from || 'any',
          game_assets: normalizeMiniGameNaveAssets(nextInteraction.game_assets)
        }
      : emptyInteractionState());
  };

  const updateInteractionOnTargetTile = (nextInteraction) => {
    if (!interactionTarget) return;
    const coordinate = Number(interactionTarget.coordinate) || 0;
    const layer = Number(interactionTarget.layer) || 0;
    setTiles((current) => current.map((tile) => {
      if (Number(tile.coordinate) !== coordinate || Number(tile.layer) !== layer) return tile;
      const nextMetadata = tile.metadata && typeof tile.metadata === 'object' ? { ...tile.metadata } : {};
      if (nextInteraction) {
        nextMetadata.interaction = nextInteraction;
      } else {
        delete nextMetadata.interaction;
      }
      return {
        ...tile,
        metadata: nextMetadata
      };
    }));
  };

  const applyEyedropper = (coordinate) => {
    const tile = getTileAtCoordinate(coordinate);
    if (!tile) return;
    hydrateEditingStateFromTile(tile);
    setActiveTool('paint');
  };

  useEffect(() => {
    if (sidebarTab !== 'interaction' || !interactionTarget) return;
    const nextInteraction = interactionState.enabled ? normalizeInteractionMetadata(interactionState) : null;
    updateInteractionOnTargetTile(nextInteraction);
  }, [interactionState, interactionTarget, sidebarTab]);

  const getCanvasCoordinateFromPointerEvent = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) * (canvas.width / rect.width) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) * (canvas.height / rect.height) / CELL_SIZE);
    const width = Math.max(1, Number(mapWidth) || 32);
    const height = Math.max(1, Number(mapHeight) || 20);
    if (x < 0 || y < 0 || x >= width || y >= height) return null;
    return {
      x,
      y,
      width,
      height,
      coordinate: y * width + x
    };
  };

  const handleCanvasPointer = (e) => {
    const pointer = getCanvasCoordinateFromPointerEvent(e);
    if (!pointer) return;
    const { coordinate, width, height } = pointer;
    setHoverCoordinate(coordinate);

    if (e.type === 'mousedown') {
      if (activeTool === 'eyedropper') {
        applyEyedropper(coordinate);
        return;
      }
      if (activeTool === 'select') {
        setDragStartCoordinate(coordinate);
        setSelectionRect(normalizeRectSelection(coordinate, coordinate, width, height));
        return;
      }
      if ((activeTool === 'paint' || activeTool === 'erase') && !strokeUndoCapturedRef.current) {
        captureUndoSnapshot();
        strokeUndoCapturedRef.current = true;
      }
      setDragPainting(true);
      updateTileAt(coordinate);
    } else if (activeTool === 'select' && e.type === 'mousemove' && e.buttons === 1 && dragStartCoordinate !== null) {
      setSelectionRect(normalizeRectSelection(dragStartCoordinate, coordinate, width, height));
    } else if ((activeTool === 'paint' || activeTool === 'erase') && e.type === 'mousemove' && e.buttons === 1 && dragPainting) {
      updateTileAt(coordinate);
    }
  };

  const handleCanvasContextMenu = (e) => {
    const pointer = getCanvasCoordinateFromPointerEvent(e);
    if (!pointer) return;
    e.preventDefault();
    const tile = getTileAtCoordinate(pointer.coordinate);
    if (!tile) return;
    hydrateEditingStateFromTile(tile);
    setSidebarTab('interaction');
  };

  useEffect(() => {
    if (!dragPainting) return undefined;
    const stopPainting = () => setDragPainting(false);
    const releaseStroke = () => {
      strokeUndoCapturedRef.current = false;
    };
    window.addEventListener('mouseup', stopPainting);
    window.addEventListener('mouseup', releaseStroke);
    return () => {
      window.removeEventListener('mouseup', stopPainting);
      window.removeEventListener('mouseup', releaseStroke);
    };
  }, [dragPainting]);

  useEffect(() => {
    if (activeTool !== 'select') return undefined;
    const stopSelection = () => {
      setDragStartCoordinate(null);
    };
    window.addEventListener('mouseup', stopSelection);
    return () => window.removeEventListener('mouseup', stopSelection);
  }, [activeTool]);

  const clearLayer = () => {
    captureUndoSnapshot();
    setTiles((current) => current.filter((tile) => Number(tile.layer) !== Number(activeLayer)));
  };

  const fillLayer = () => {
    const width = Math.max(1, Number(mapWidth) || 32);
    const height = Math.max(1, Number(mapHeight) || 20);
    captureUndoSnapshot();
    const nextTiles = [];
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        nextTiles.push({
          coordinate: (y * width) + x,
          layer: Number(activeLayer) || 0,
          tileset_role: activeRoleValue,
          tile_index: Number(selectedTileIndex) || 0,
          blocked: Boolean(paintBlocked),
          metadata: buildPaintMetadata()
        });
      }
    }
    setTiles((current) => [
      ...current.filter((tile) => Number(tile.layer) !== Number(activeLayer)),
      ...nextTiles
    ].sort((a, b) => (Number(a.layer) - Number(b.layer)) || (Number(a.coordinate) - Number(b.coordinate))));
  };

  const handleSave = () => {
    if (!city?.id || !onSaveMap) return;
    onSaveMap(city.id, {
      width: Number(mapWidth) || 32,
      height: Number(mapHeight) || 20,
      tiles: tiles.map(normalizeTile)
    });
  };

  if (!city) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-950/40 p-6 text-slate-500">
        Selecciona una ciudad para editar su Cúpula.
      </div>
    );
  }

  if (!config) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-950/40 p-6 text-slate-500">
        Esta ciudad todavía no tiene perfil RPG. Configúrala primero para poder editar el mapa.
      </div>
    );
  }

  const selectedTileset = activeTileset || assignedTilesets[0] || null;
  const paletteLength = getPaletteLength(selectedTileset, imageInfoRef.current[activeRoleValue]);

  return (
    <div className="space-y-6">
      <section className="bg-[#11132d] p-6 rounded-3xl border border-slate-800 shadow-xl space-y-5">
        <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-cyan-300">
              <MapPinned size={12} /> Editor de Cúpula
            </div>
            <h2 className="text-2xl font-bold text-white">Mapa interior de {city.name}</h2>
            <p className="text-sm text-slate-400 max-w-3xl">
              Este mapa es independiente del editor de sectores. Aqui pintas la Cúpula por capas y cada tile se guarda por coordenada.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={undoLastChange}
              disabled={undoDepth === 0}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-white hover:border-violet-400 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RefreshCw size={16} className="rotate-180" /> Deshacer{undoDepth > 0 ? ` (${undoDepth})` : ''}
            </button>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-white hover:border-cyan-400 hover:bg-slate-800"
            >
              <RefreshCw size={16} /> Recargar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-xl bg-game-primary px-4 py-3 text-sm font-bold text-white hover:bg-game-primary/80 disabled:opacity-50"
            >
              <Save size={16} /> {isSaving ? 'Guardando...' : 'Guardar mapa'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">Ciudad</div>
            <div className="mt-2 text-lg font-bold text-white truncate">{city.name}</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">Tamano</div>
            <div className="mt-2 text-lg font-bold text-white">{mapWidth} x {mapHeight}</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">Tiles pintados</div>
            <div className="mt-2 text-lg font-bold text-white">{tiles.length}</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">Roles activos</div>
            <div className="mt-2 text-lg font-bold text-white">{assignedTilesets.length}</div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <section className="space-y-4 min-w-0">
          <div className="rounded-3xl border border-slate-800 bg-[#11132d] p-4 space-y-4 shadow-xl">
            <div className="flex flex-wrap items-center gap-3">
              <label className="space-y-1">
                <span className="block text-[11px] font-bold uppercase tracking-widest text-slate-500">Ancho</span>
                <input
                  type="number"
                  min={8}
                  value={mapWidth}
                  onChange={(e) => {
                    const nextWidth = Math.max(8, Number(e.target.value) || 32);
                    setMapWidth(nextWidth);
                    setTiles((current) => current.filter((tile) => {
                      const x = Number(tile.coordinate) % nextWidth;
                      const y = Math.floor(Number(tile.coordinate) / Math.max(1, Number(mapWidth) || 32));
                      return x < nextWidth && y < Math.max(8, Number(mapHeight) || 20);
                    }));
                  }}
                  className="w-28 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                />
              </label>
              <label className="space-y-1">
                <span className="block text-[11px] font-bold uppercase tracking-widest text-slate-500">Alto</span>
                <input
                  type="number"
                  min={8}
                  value={mapHeight}
                  onChange={(e) => {
                    const nextHeight = Math.max(8, Number(e.target.value) || 20);
                    setMapHeight(nextHeight);
                    setTiles((current) => current.filter((tile) => {
                      const x = Number(tile.coordinate) % Math.max(1, Number(mapWidth) || 32);
                      const y = Math.floor(Number(tile.coordinate) / Math.max(1, Number(mapWidth) || 32));
                      return x < Math.max(8, Number(mapWidth) || 32) && y < nextHeight;
                    }));
                  }}
                  className="w-28 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                />
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={paintBlocked}
                  onChange={(e) => setPaintBlocked(e.target.checked)}
                  className="h-4 w-4 accent-cyan-500"
                />
                Bloqueante
              </label>
              <div className="flex flex-wrap gap-2 rounded-xl border border-slate-700 bg-slate-900/50 p-1">
                <button
                  type="button"
                  onClick={() => setActiveTool('paint')}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition-colors ${
                    activeTool === 'paint'
                      ? 'bg-game-primary text-white'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  <Layers size={16} /> Pintar
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTool('erase')}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition-colors ${
                    activeTool === 'erase'
                      ? 'bg-game-primary text-white'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  <Trash2 size={16} /> Borrador
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTool('eyedropper')}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition-colors ${
                    activeTool === 'eyedropper'
                      ? 'bg-game-primary text-white'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  <Pipette size={16} /> Cuentagotas
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTool('select')}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition-colors ${
                    activeTool === 'select'
                      ? 'bg-game-primary text-white'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  <Layers size={16} /> Seleccionar zona
                </button>
              </div>
              <button
                type="button"
                onClick={fillLayer}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-white hover:border-game-primary hover:bg-slate-800"
              >
                <Layers size={16} /> Rellenar capa
              </button>
              <button
                type="button"
                onClick={clearLayer}
                className="inline-flex items-center gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-bold text-rose-200 hover:bg-rose-500/20"
              >
                <Trash2 size={16} /> Limpiar capa
              </button>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-3">
              <div className="mb-3 flex flex-wrap gap-2">
                {['Suelo', 'Capa media', 'Superior'].map((label, index) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setActiveLayer(index)}
                    className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors ${
                      Number(activeLayer) === index
                        ? 'bg-game-primary text-white'
                        : 'border border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

                <div className="max-h-[70vh] max-w-full overflow-auto rounded-2xl border border-slate-800 bg-black/20">
                  <canvas
                    ref={canvasRef}
                    className="block min-w-max cursor-crosshair"
                    onMouseDown={handleCanvasPointer}
                    onMouseMove={handleCanvasPointer}
                    onContextMenu={handleCanvasContextMenu}
                    onMouseLeave={() => setHoverCoordinate(null)}
                  />
                </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
                <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1">
                  Capa: {Number(activeLayer) === 0 ? 'Suelo' : Number(activeLayer) === 1 ? 'Capa media' : 'Superior'}
                </span>
                <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1">
                  Coord: {hoverCoordinate !== null ? hoverCoordinate : '-'}
                </span>
                <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1">
                  Tilesets: {assignedTilesets.length}
                </span>
                <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1">
                  Herramienta: {activeTool === 'eyedropper' ? 'Cuentagotas' : activeTool === 'erase' ? 'Borrador' : activeTool === 'select' ? 'Seleccionar' : 'Pintar'}
                </span>
              </div>

              {selectionRect && (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-violet-500/20 bg-violet-500/10 p-3">
                  <span className="rounded-full border border-violet-400/30 bg-violet-500/20 px-3 py-1 text-xs font-bold text-violet-100">
                    Selección: {selectionRect.x1},{selectionRect.y1} - {selectionRect.x2},{selectionRect.y2}
                  </span>
                  <button
                    type="button"
                    onClick={() => applyTileSelection(selectionRect, 'paint')}
                    className="rounded-xl border border-violet-400/30 bg-violet-500/20 px-3 py-2 text-xs font-bold text-violet-100 hover:bg-violet-500/30"
                  >
                    Rellenar selección
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTileSelection(selectionRect, 'erase')}
                    className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-bold text-rose-200 hover:bg-rose-500/20"
                  >
                    Borrar selección
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectionRect(null);
                      setDragStartCoordinate(null);
                    }}
                    className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold text-slate-300 hover:border-slate-500 hover:text-white"
                  >
                    Limpiar selección
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="space-y-4 min-w-0">
          <section className="rounded-3xl border border-slate-800 bg-[#11132d] p-4 space-y-4 shadow-xl">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <Castle size={22} className="text-cyan-300" />
              <div>
                <h3 className="font-bold text-white">Tileset activo</h3>
                <p className="text-xs text-slate-500">Elige el conjunto que vas a pintar en la Cúpula.</p>
              </div>
            </div>

              <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-4">
                <button
                  type="button"
                  onClick={() => setSidebarTab('tileset')}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                    sidebarTab === 'tileset'
                      ? 'bg-game-primary text-white'
                      : 'border border-slate-700 bg-slate-900/60 text-slate-400 hover:border-slate-500 hover:text-white'
                  }`}
                >
                  Tileset
                </button>
                <button
                  type="button"
                  onClick={() => setSidebarTab('interaction')}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                    sidebarTab === 'interaction'
                      ? 'bg-game-primary text-white'
                      : 'border border-slate-700 bg-slate-900/60 text-slate-400 hover:border-slate-500 hover:text-white'
                  }`}
                >
                  Interacción
                </button>
              </div>

              {sidebarTab === 'tileset' ? (
                <>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Tileset / capa</label>
                    <select
                      value={activeTileset?.assignment_key || ''}
                      onChange={(e) => {
                        const nextKey = e.target.value;
                        const nextTileset = tilesetByKey.get(nextKey) || assignedTilesets.find((tileset) => tileset.assignment_key === nextKey) || null;
                        setActiveTilesetKey(nextTileset?.assignment_key || '');
                        const nextCatalogIndex = getTilesetCatalog(nextTileset)[0]?.tile_index;
                        setSelectedTileIndex(Number.isInteger(Number(nextCatalogIndex)) ? Number(nextCatalogIndex) : 0);
                        setInteractionTarget(null);
                        setActiveTool('paint');
                      }}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                    >
                      {assignedTilesets.map((tileset) => (
                        <option key={tileset.assignment_key} value={tileset.assignment_key}>
                          {tileset.name} · {tileset.role}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Tile seleccionado</label>
                    <input
                      type="number"
                      min={0}
                      value={selectedTileIndex}
                      onChange={(e) => selectTile(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                    />
                  </div>

                  {selectedTileset ? (
                    <TilePalette
                      tileset={selectedTileset}
                      selectedTileIndex={selectedTileIndex}
                      onSelectTile={selectTile}
                    />
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-4 text-sm text-slate-500">
                      No hay tileset disponible para el rol seleccionado.
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <section className="rounded-2xl border border-slate-800 bg-[#11132d] p-4 space-y-4 shadow-xl">
                    <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                      <MapPinned size={20} className="text-cyan-300" />
                      <div>
                        <h3 className="font-bold text-white">Interaccion del tile</h3>
                        <p className="text-xs text-slate-500">Define edificios y puntos usables dentro de la Cupula.</p>
                      </div>
                    </div>
                    <label className="flex items-center justify-between rounded-2xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-200">
                      <span>Activar interaccion</span>
                      <input
                        type="checkbox"
                        checked={interactionState.enabled}
                        onChange={(e) => setInteractionState((current) => ({
                          ...current,
                          enabled: e.target.checked
                        }))}
                        className="h-4 w-4 accent-cyan-500"
                      />
                    </label>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Tipo</label>
                      <select
                        value={interactionState.type}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          const nextPreset = INTERACTION_TYPES.find((entry) => entry.value === nextType);
                          const nextAssets = nextType === 'mini-juego-nave'
                            ? normalizeMiniGameNaveAssets((interactionState.game_assets && typeof interactionState.game_assets === 'object') ? interactionState.game_assets : {})
                            : (interactionState.game_assets || normalizeMiniGameNaveAssets());
                          setInteractionState((current) => ({
                            ...current,
                            type: nextType,
                            target: nextPreset ? nextPreset.target : current.target || '',
                            game_assets: nextAssets
                          }));
                        }}
                        className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                      >
                        {INTERACTION_TYPES.map((entry) => (
                          <option key={entry.value} value={entry.value}>
                            {entry.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Acceso desde</label>
                      <select
                        value={interactionState.access_from || 'any'}
                        onChange={(e) => setInteractionState((current) => ({
                          ...current,
                          access_from: e.target.value
                        }))}
                        className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                      >
                        {ACCESS_DIRECTIONS.map((entry) => (
                          <option key={entry.value} value={entry.value}>
                            {entry.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-500">
                        Úsalo para puertas y entradas: indica desde qué lado puede activarse el tile.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Nombre</label>
                      <input
                        type="text"
                        value={interactionState.label}
                        onChange={(e) => setInteractionState((current) => ({ ...current, label: e.target.value }))}
                        placeholder="Ej: Tienda central"
                        className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                        {['note', 'entry'].includes(interactionState.type) ? 'Texto del modal' : 'Descripción'}
                      </label>
                      <textarea
                        rows={3}
                        value={interactionState.description}
                        onChange={(e) => setInteractionState((current) => ({ ...current, description: e.target.value }))}
                        placeholder={['note', 'entry'].includes(interactionState.type) ? 'Texto que vera el jugador en el modal.' : 'Texto breve que vera el jugador.'}
                        className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Destino / accion</label>
                      <input
                        type="text"
                        value={interactionState.target}
                        onChange={(e) => setInteractionState((current) => ({ ...current, target: e.target.value }))}
                        placeholder="/city/shop o identificador propio"
                        className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                      />
                    </div>
                    {isMiniGameNave && (
                      <div className="space-y-3 rounded-2xl border border-cyan-400/20 bg-cyan-500/5 p-4">
                        <div className="text-xs font-bold uppercase tracking-widest text-cyan-200">
                          Assets del minijuego nave
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Fondo</label>
                          <input
                            type="text"
                            value={miniGameAssets.background_image}
                            onChange={(e) => updateMiniGameAssets({ background_image: e.target.value })}
                            placeholder="fondo-nave.jpg o /img/fondo.jpg"
                            className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Nave del jugador</label>
                          <input
                            type="text"
                            value={miniGameAssets.player_image}
                            onChange={(e) => updateMiniGameAssets({ player_image: e.target.value })}
                            placeholder="nave-jugador.png"
                            className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Enemigos</label>
                          <textarea
                            rows={3}
                            value={miniGameAssets.enemy_images.join(', ')}
                            onChange={(e) => updateMiniGameAssets({ enemy_images: parseMiniGameAssetList(e.target.value) })}
                            placeholder="enemigo-1.png, enemigo-2.png, enemigo-3.png"
                            className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                          />
                          <p className="text-[11px] text-slate-500">
                            Puedes separar varios sprites con coma, punto y coma o saltos de línea.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Disparo</label>
                            <input
                              type="text"
                              value={miniGameAssets.bullet_image}
                              onChange={(e) => updateMiniGameAssets({ bullet_image: e.target.value })}
                              placeholder="bala.png"
                              className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Explosion</label>
                            <input
                              type="text"
                              value={miniGameAssets.explosion_image}
                              onChange={(e) => updateMiniGameAssets({ explosion_image: e.target.value })}
                              placeholder="explosion.png"
                              className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                            />
                          </div>
                        </div>
                        <p className="text-[11px] text-cyan-100/80">
                          Consejo: usa sprites verticales pequeños para enemigo y jugador; si dejas un campo vacío, el juego dibuja una versión retro por defecto.
                        </p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setInteractionState(emptyInteractionState())}
                        className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-bold text-rose-200 hover:bg-rose-500/20"
                      >
                        Limpiar interaccion
                      </button>
                      <button
                        type="button"
                        onClick={() => setInteractionState((current) => ({ ...current, enabled: false }))}
                        className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold text-slate-300 hover:border-slate-500 hover:text-white"
                      >
                        Desactivar
                      </button>
                    </div>
                    <div className="rounded-2xl border border-cyan-400/15 bg-cyan-500/5 p-3 text-xs text-slate-300">
                      Esta configuracion se guarda en la metadata del tile cuando pintas, rellenas o copias con cuentagotas.
                    </div>
                    <div className="rounded-2xl border border-violet-500/20 bg-violet-500/10 p-3 text-xs text-violet-100">
                      Clic derecho en un tile para cargar su interaccion y editarla. Mientras estes en la pestaña `Tileset`, pintar o borrar ya no copia la interaccion.
                    </div>
                  </section>
                </div>
              )}
            </section>

          <section className="rounded-3xl border border-slate-800 bg-[#11132d] p-4 space-y-3 shadow-xl">
            <h3 className="flex items-center gap-2 text-lg font-bold text-cyan-300">
              <ImageIcon size={18} /> Ayuda rapida
            </h3>
            <div className="space-y-2 text-sm text-slate-300">
              <p>1. Elige un rol de tileset.</p>
              <p>2. Selecciona el tile desde el catalogo o la vista del sprite sheet.</p>
              <p>3. Usa el cuentagotas, el borrador o la selección rectangular según lo que quieras corregir.</p>
              <p>4. Pinta por capas y guarda la Cúpula.</p>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
